import httpClient from '../../../lib/api';
import {
  Review360PerformanceQuery,
  Review360PerformanceApiInterface,
  Review360PerformanceItem,
  Review360PerformanceData,
} from '../type';
import store from '../../../state/store';

/**
 * API Resources configuration
 */
interface ApiResources {
  myReview: {
    url: string;
  };
  myReviewerAssignments: {
    url: string;
  };
}

/**
 * Person data structure (used for employee)
 */
export interface PersonData {
  id: number;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
}

/**
 * Progress information for a review
 */
export interface ReviewProgress {
  totalQuestions: number;
  completedQuestions: number;
}

/**
 * Section information
 */
export interface SectionData {
  title: string;
  description: string;
}

/**
 * Single reviewer assignment from the new API
 */
export interface ReviewerAssignment {
  reviewId: number;
  performanceId: number;
  sectionId: number;
  employee: PersonData;
  section: SectionData;
  status: string;
  startDate: string | null;
  dueDate: string | null;
  lastActivity: string | null;
  progress: ReviewProgress | null;
  submissionDate: string | null;
  appraisalTitle: string;
}

/**
 * Response structure from the new reviewer assignments endpoint
 */
export interface ReviewerAssignmentsResponse {
  reviews: ReviewerAssignment[];
  totalCount: number;
}

export const resources: ApiResources = {
  myReview: {
    url: '/performance-api/v1/review-360-performances',
  },
  myReviewerAssignments: {
    url: '/performance-api/v1/performances/reviews',
  },
};

/**
 * Convert legacy sort format to the new API sortBy format.
 * Legacy: 'fieldName' (ascending) or '-fieldName' (descending)
 * New API: 'fieldName:asc' or 'fieldName:desc'
 */
function toSortByParam(sort: string | undefined): string | undefined {
  if (!sort) return undefined;
  if (sort.startsWith('-')) {
    return `${sort.substring(1)}:desc`;
  }
  return `${sort}:asc`;
}

/**
 * Transform new API response to legacy format
 * Converts the reviewer assignments response to match the expected Review360PerformanceItem structure
 */
function transformReviewerAssignmentToPerformanceItem(
  assignment: ReviewerAssignment
): Review360PerformanceItem {
  const startDate = assignment.startDate ? new Date(assignment.startDate).getTime() / 1000 : 0;
  const dueDate = assignment.dueDate ? new Date(assignment.dueDate).getTime() / 1000 : 0;

  return {
    id: assignment.performanceId,
    name: {
      id: assignment.employee.id,
      firstName: assignment.employee.firstName,
      lastName: assignment.employee.lastName,
      profileImage: assignment.employee.profileImageUrl || '',
    },
    // Manager field removed from new API - provide empty default for backward compatibility
    manager: {
      id: 0,
      firstName: '',
      lastName: '',
      profileImage: '',
    },
    title: assignment.appraisalTitle || '',
    status: assignment.status || '',
    definedStartDate: startDate,
    definedDueDate: dueDate,
  };
}

export class Review360PerformanceApi implements Review360PerformanceApiInterface {
  /**
   * Check if the new performance appraisal UI feature flag is enabled
   */
  private readonly isNewAppraisalUiEnabled = (): boolean => {
    const state = store.getState();
    const isEnabled =
      state.app?.systemConfiguration?.featureFlags?.frontendNewPerformanceAppraisalUi === true;
    return isEnabled;
  };

  readonly getReview360Performance = (
    query: Review360PerformanceQuery
  ): Promise<Review360PerformanceData> => {
    // Use new endpoint if feature flag is enabled
    if (this.isNewAppraisalUiEnabled()) {
      return this.getReview360PerformanceFromReviewerAssignments(query);
    }

    // Legacy endpoint
    return httpClient
      .get(resources.myReview.url, {
        params: {
          // params mapping
          page: query.page,
          limit: query.limit,
          sort: query.sort ? query.sort : undefined,
          keyword: query.keyword ? query.keyword : undefined,
        },
      })
      .then(result => {
        const total =
          typeof result.headers !== 'undefined' &&
          typeof result.headers['x-pagination-count'] !== 'undefined'
            ? result.headers['x-pagination-count']
            : 0;
        const pageNum =
          typeof result.headers !== 'undefined' &&
          typeof result.headers['x-pagination-page'] !== 'undefined'
            ? result.headers['x-pagination-page']
            : query.page;
        const limitNum =
          typeof result.headers !== 'undefined' &&
          typeof result.headers['x-pagination-limit'] !== 'undefined'
            ? result.headers['x-pagination-limit']
            : query.limit;
        return Promise.resolve({
          total,
          page: pageNum,
          limit: limitNum,
          data: typeof result.data !== 'undefined' ? result.data : [],
        });
      })
      .catch((error: Error) => Promise.reject(error));
  };

  /**
   * Fetch 360 review performances from the new reviewer assignments endpoint
   * Transforms the response to match the legacy format
   */
  private readonly getReview360PerformanceFromReviewerAssignments = (
    query: Review360PerformanceQuery
  ): Promise<Review360PerformanceData> => {
    const { url } = resources.myReviewerAssignments;
    const params = {
      page: query.page,
      itemsPerPage: query.limit,
      search: query.keyword ? query.keyword : undefined,
      // Use explicit checks: status 0 ("Not Started") is valid and must not be treated as falsy
      status:
        query.status !== undefined && query.status !== null && `${query.status}` !== ''
          ? query.status
          : undefined,
      sortBy: toSortByParam(query.sort),
    };

    return httpClient
      .get(url, { params })
      .then(result => {
        const responseData = result.data || { reviews: [], totalCount: 0 };
        const reviews = responseData.reviews || [];
        const totalCount = responseData.totalCount || 0;

        // Transform the new response format to match the legacy format
        const transformedData = reviews.map((assignment: ReviewerAssignment) =>
          transformReviewerAssignmentToPerformanceItem(assignment)
        );

        return Promise.resolve({
          total: totalCount,
          page: query.page,
          limit: query.limit,
          data: transformedData,
        });
      })
      .catch((error: Error) => Promise.reject(error));
  };
}

export default new Review360PerformanceApi();
